/**
 * @file Reads the analytics calendar for one answered result or home summary
 * and turns a goal read with the booked and quoted beside it into what a goal
 * cell renders. Consumers: the Overview panel's tiles, the person and team
 * tables and `useHomeGoalProgress`, each passing the result or summary its own
 * figures came from, so every goal is read in the period and fiscal year of
 * the answer it is set against, and Home and the Overview read goals alike. A calendar error
 * fails every cell, and no result or calendar yet keeps it loading, never
 * zero.
 */
import type { Snapshot } from 'valtio';

import type { NamedQueryResult } from '#types';

import { analyticsGoalReadProgress } from './analyticsGoalReadProgress';
import type {
  AnalyticsGoalRead,
  AnalyticsGoalReadProgress,
} from './analyticsOverviewTab.types';
import type { AnalyticsDecimalFigure } from './index.types';
import { useAnalyticsFiscalCalendar } from './useAnalyticsFiscalCalendar';

export const useAnalyticsGoalProgress = (
  result: Snapshot<Pick<NamedQueryResult, 'period'>> | undefined,
): ReturnType<typeof useAnalyticsFiscalCalendar> & {
  progressOf: (
    read: AnalyticsGoalRead,
    booked: string,
    quoted: AnalyticsDecimalFigure,
  ) => AnalyticsGoalReadProgress;
} => {
  const calendar = useAnalyticsFiscalCalendar(result?.period.to);
  const { timeZone, periodStart, calendarError } = calendar;
  return {
    ...calendar,
    progressOf: (read, booked, quoted) => {
      if (calendarError !== null)
        return { status: 'failed', message: calendarError };
      if (!result || !timeZone || periodStart === null)
        return { status: 'loading' };
      return analyticsGoalReadProgress(read, {
        booked,
        quoted: quoted.status === 'answered' ? quoted.decimal : null,
        period: result.period,
        calendar: { timeZone },
      });
    },
  };
};
